import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { CostCode, ProjectDoc } from 'common';
import { ProjectService } from 'src/projects/services/ProjectService';
import { DEFAULT_COST_CODES } from './default-cost-codes';

@Component({
  tag: 'cost-code-selector',
})
export class CostCodeSelector {
  static async present(projectId: string, onSelected: (projectCode: any) => void): Promise<void> {
    const modal = await modalController.create({
      component: 'cost-code-selector',
      componentProps: { projectId },
      showBackdrop: true,
      cssClass: 'modal-height-100',
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    modal.addEventListener('selected', (e: CustomEvent) => {
      onSelected(e.detail as any);
      modal.dismiss();
    });

    modal.present();
  }

  static async presentWithPromise(projectId: string): Promise<CostCode> {
    const modal = await modalController.create({
      component: 'cost-code-selector',
      componentProps: { projectId },
      showBackdrop: true,
    });

    return new Promise(resolve => {
      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
      modal.addEventListener('selected', (e: CustomEvent) => {
        resolve(e.detail as CostCode);
        modal.dismiss();
      });
      modal.present();
    });
  }

  @Prop() projectId: string;

  @Event() selected: EventEmitter<CostCode>;
  @Event() dismiss: EventEmitter;

  @State() searchTerm: string = '';
  @State() filteredCodes: CostCode[] = [];
  @State() selectedDivision: string | null = null;

  private costCodes: CostCode[];
  private divisions: string[] = [];

  async componentWillLoad() {
    const db = ProjectService.getProjectDb(this.projectId);
    const doc: ProjectDoc = await db.getDoc(this.projectId);
    if (doc) {
      this.handleProjectDoc(doc);
    } else {
      db.addListener('complete', docs => this.handleProjectDoc(docs[0] as ProjectDoc));
    }
  }

  private handleProjectDoc(doc: ProjectDoc) {
    this.costCodes = doc.cost_codes || DEFAULT_COST_CODES;
    // Extract unique divisions
    this.divisions = [...new Set(this.costCodes.map(code => code.division_name))];
    // Start in division view
    this.filteredCodes = [];
  }

  private handleSearch = (searchTerm: string) => {
    this.searchTerm = searchTerm;

    if (searchTerm === '') {
      // Return to division view
      this.selectedDivision = null;
      this.filteredCodes = [];
    } else {
      // Show all matching cost codes
      this.filteredCodes = this.costCodes.filter(
        code =>
          code.code_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          code.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          code.division_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
  };

  private handleDivisionSelect = (division: string) => {
    this.selectedDivision = division;
    this.filteredCodes = this.costCodes.filter(code => code.division_name === division);
  };

  private handleBackToDivisions = () => {
    this.selectedDivision = null;
    this.filteredCodes = [];
  };

  private handleCodeSelect = (code: any) => {
    this.selected.emit(code);
  };

  render() {
    const modalTitle = this.selectedDivision || 'Select Project Code';
    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={modalTitle}
          enableSearch={true}
          searchPlaceholder="Search Project Codes"
          searchValue={this.searchTerm}
          showBack={!!this.selectedDivision && this.searchTerm === ''}
          onDismiss={() => this.dismiss.emit()}
          onBack={() => this.handleBackToDivisions()}
          onSearchInput={(e: CustomEvent) => this.handleSearch(e.detail)}
        />
        <ion-content class="cc-scrollbars">
          {/* marginBottom so the user can see they have reached the end of the list */}
          <ion-list class="cc-no-line-list" style={{ marginBottom: '30px' }}>
            {this.searchTerm === '' && !this.selectedDivision
              ? // Division view
                this.divisions.map(division => (
                  <ion-item button onClick={() => this.handleDivisionSelect(division)} key={division}>
                    <p>{division}</p>
                    <ion-icon name="chevron-forward-outline" slot="end" color="light" />
                  </ion-item>
                ))
              : this.selectedDivision && this.searchTerm === ''
                ? // Selected division view
                  this.filteredCodes.map(code => (
                    <ion-item button onClick={() => this.handleCodeSelect(code)} key={code.code_number}>
                      <p>
                        {code.code_number} - {code.description}
                      </p>
                    </ion-item>
                  ))
                : // Search results view
                  this.filteredCodes.map(code => (
                    <ion-item button onClick={() => this.handleCodeSelect(code)} key={code.code_number}>
                      <p>{code.division_name}</p>
                      <ion-icon name="chevron-forward-outline" color="light" size="small" />
                      <p>
                        {code.code_number} - {code.description}
                      </p>
                    </ion-item>
                  ))}
          </ion-list>
        </ion-content>
      </Host>
    );
  }
}
